import React from 'react';
import { ServiceItem, LocalizedText } from '../types/profile';
import { useLocalization } from '../engine/localization/context';
import { ExternalLink, Layers, Sparkles } from 'lucide-react';

interface ServicesListProps {
  services: ServiceItem[];
  title?: LocalizedText;
}

export const ServicesList: React.FC<ServicesListProps> = ({ services, title }) => {
  const { resolve, t, direction } = useLocalization();

  if (!services || services.length === 0) return null;

  const sectionHeading = title ? resolve(title) : t('services.title');

  return (
    <section aria-label="Services & Solutions" className="my-6">
      <div className="flex items-center justify-between mb-3 px-1">
        <h2 className="text-base font-bold text-white flex items-center gap-2">
          <Layers className="w-4 h-4 text-sky-400" />
          <span>{sectionHeading}</span>
        </h2>
        <span className="text-[11px] font-medium text-slate-400">
          {services.length} {direction === 'rtl' ? 'منتجات/خدمات' : 'Products/Services'}
        </span>
      </div>

      <div className="space-y-3">
        {services.map((item) => {
          const title = resolve(item.title);
          const description = resolve(item.description);
          const badge = item.badge ? resolve(item.badge) : null;

          const CardContent = (
            <div
              className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl transition-all duration-200 backdrop-blur-md border ${
                item.highlighted
                  ? 'bg-gradient-to-br from-sky-950/40 to-slate-900/80 border-sky-500/30 hover:border-sky-400/60 shadow-[0_4px_20px_rgba(56,189,248,0.1)]'
                  : 'bg-slate-900/50 hover:bg-slate-800/60 border-slate-800/80 hover:border-slate-700/80 shadow-sm'
              } flex flex-col gap-1.5`}
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="text-sm font-bold text-slate-100 flex items-center gap-1.5 leading-snug">
                  {item.highlighted && <Sparkles className="w-3.5 h-3.5 text-amber-400 shrink-0" />}
                  <span>{title}</span>
                </h3>

                {badge && (
                  <span className="shrink-0 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-500/20 text-sky-300 border border-sky-500/30">
                    {badge}
                  </span>
                )}
              </div>

              <p className="text-xs text-slate-300/80 leading-relaxed">
                {description}
              </p>

              {item.linkUrl && (
                <div className="flex items-center gap-1 text-[11px] font-semibold text-sky-400 hover:text-sky-300 mt-1 self-start">
                  <span>{direction === 'rtl' ? 'تفاصيل الخدمة' : 'Learn More'}</span>
                  <ExternalLink className="w-3 h-3" />
                </div>
              )}
            </div>
          );

          if (item.linkUrl) {
            return (
              <a
                key={item.id}
                href={item.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block group"
              >
                {CardContent}
              </a>
            );
          }

          return <div key={item.id}>{CardContent}</div>;
        })}
      </div>
    </section>
  );
};

